import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { createEmptyEnum, createEmptyMessageType } from "../../../utils/pdApiUtils";
import { EditActions, editActions } from "../../../actions/editActions";
import EnumTable from "../../common/EnumTable/EnumTable";
import ActionMenu from "../../common/ActionMenu/ActionMenu";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import NavigationLink from "../../../components/NavigationLink/NavigationLink";
import ContentLayout from "../../../components/ContentLayout/ContentLayout";
import { parseUrlToName, parseNameToUrl } from "../../../utils/utils";
import EditMessageType from "../../Editing/EditMessageType";
import { ColumnFilter } from "@tanstack/react-table";
type Filter = ColumnFilter;
type StateProps = {
  pdApi: State.PdApi | undefined;
  filterOptions: State.FilterOptions;
};

type DispatchProps = {
  editActions: typeof editActions;
};
type OwnProps = {
  toggleModal: () => {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  match: any;
};
// Props of the component
export type EventEnumerationsProps = StateProps & DispatchProps & OwnProps & {
  objectIdentifier: string;
};

type EventEnumerationsState = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeMenu: State.EventEnumerationsSubmenu;
  isModalOpen: boolean;
  tableColumnFilters: {
    [key in State.EventEnumerationsSubmenu]: Filter[];
  };
};

export class EventEnumerations extends React.Component<
  EventEnumerationsProps,
  EventEnumerationsState
> {
  subMenus: State.EventEnumerationsSubmenu[] = [
    "Event Classes",
    "Event Types",
    "Message Types",
    "Event Pids",
    "Ext. Commands"
  ];

  subPages: Internal.SubPages = {
    "Event Classes": {
      itemName: "event class",
      PdApiField: "eventClasses",
      dataView: EnumTable,
      createEmpty: createEmptyEnum,
      setExtraProperties: null
    },
    "Event Types": {
      itemName: "event type",
      PdApiField: "eventTypes",
      dataView: EnumTable,
      createEmpty: createEmptyEnum,
      setExtraProperties: null
    },
    "Message Types": {
      itemName: "message type",
      PdApiField: "messageTypes", // TODO: temporarily using eventPids until messageTypes API is available
      dataView: EnumTable,
      createEmpty: createEmptyMessageType,
      setExtraProperties: null,
      editor: EditMessageType
    },
    "Event Pids": {
      itemName: "event pid",
      PdApiField: "eventPids",
      dataView: EnumTable,
      createEmpty: createEmptyEnum,
      setExtraProperties: null
    },
    "Ext. Commands": {
      itemName: "command",
      PdApiField: "extCommands",
      dataView: EnumTable,
      createEmpty: createEmptyEnum,
      setExtraProperties: null
    }
  };

  state = {
    activeMenu: this.props.match.params.activeMenu
      ? parseUrlToName(this.props.match.params.activeMenu) as State.EventEnumerationsSubmenu
      : "Event Classes",
    isModalOpen: false,
    tableColumnFilters: {
      "Event Classes": [],
      "Event Types": [],
      "Message Types": [],
      "Event Pids": [],
      "Ext. Commands": []
    }
  };
  componentDidUpdate(): void {
    const parseActiveMenuFromURL = this.props.match.params.activeMenu
      ? parseUrlToName(this.props.match.params.activeMenu) as State.EventEnumerationsSubmenu
      : "Event Classes";
    if (this.state.activeMenu !== parseActiveMenuFromURL) {
      this.setState({ activeMenu: parseActiveMenuFromURL });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setColumnFilters = (subMenuRoute: State.EventEnumerationsSubmenu, newFilters: Filter[]): any => {
    const newFilterSet = { ...this.state.tableColumnFilters, [subMenuRoute]: newFilters };
    this.setState(prevState => ({
      ...prevState,
      tableColumnFilters: newFilterSet
    }));
  };

  render(): JSX.Element {
    const { activeMenu } = this.state;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const links = Object.keys(this.subPages).map((name: any) => (
      <NavigationLink
        key={name}
        to={"/elevator/event-enumerations/" + parseNameToUrl(name)}
        name={name}
      />
    ));
    const actionMenu = (
      <ActionMenu
        {...this.props}
        subMenuRoute={activeMenu}
        itemName={this.subPages[activeMenu].itemName}
        selectedColumnsName={activeMenu}
        dataFieldName={this.subPages[activeMenu].PdApiField}
        setExtraProperties={null}
        createEmpty={this.subPages[activeMenu].createEmpty}
        groupName="Lift"
        columnFilters={this.state.tableColumnFilters[activeMenu]}
      />
    );

    const dataView = (
      <EnumTable
        subMenuRoute={activeMenu}
        toggleModal={this.toggleModal}
        dataFieldName={this.subPages[activeMenu].PdApiField}
        setColumnFilters={this.setColumnFilters}
        editor={this.subPages[activeMenu].editor}
        />
    );
    return (
      <>
        <ContentLayout submenu={links} actionMenu={actionMenu} dataView={dataView} />
        {this.buildModal()}
      </>
    );
  }
  private toggleModal = (): void => {
    this.setState(prevState => ({
      ...prevState,
      isModalOpen: !prevState.isModalOpen
    }));
  };

  private buildModal = () => {
    const isMessageType = this.state.activeMenu === "Message Types";
    const content = (
      <span>
        {isMessageType && (
          <>
            Any existing mapping to an event class or event types will also be deleted.
            <br />
          </>
        )}
        Are you sure you want to delete this {this.subPages[this.state.activeMenu].itemName}?
        <br />
        {this.props.objectIdentifier}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={this.state.isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() =>
          this.props.editActions.delete(
            this.subPages[this.state.activeMenu].PdApiField as Api.OptionsGrud
          )
        }
        onCancel={this.toggleModal}
      />
    );
  };
}

const mapStateToProps = (state: State.Root) => ({
  pdApi: state.pdApi,
  filterOptions: state.filterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EventEnumerations);
